const zoneKeys = ['dimensions', 'metrics', 'filters', 'sort'];

const createEmptyZoneErrors = () =>
  zoneKeys.reduce((acc, key) => ({ ...acc, [key]: '' }), {});

const normalizeDataType = (value) => String(value || '').toLowerCase();

const operatorMapFromCatalog = (operatorCatalog) =>
  new Map(
    (Array.isArray(operatorCatalog) ? operatorCatalog : [])
      .filter((op) => op?.key)
      .map((op) => [String(op.key).toLowerCase(), op])
  );

const cleanValues = (values) =>
  (Array.isArray(values) ? values : [])
    .map((value) => String(value ?? '').trim())
    .filter(Boolean);

const mapFilterPayload = (filter, operatorMeta) => {
  const opKey = String(filter.operator || '').toLowerCase();
  const values = cleanValues(filter.values);
  const payload = {
    fieldKey: filter.fieldKey,
    operator: opKey,
  };

  const requiresValue = Boolean(operatorMeta?.requiresValue);
  const requiresSecondValue = Boolean(operatorMeta?.requiresSecondValue);
  const supportsMultipleValues = Boolean(operatorMeta?.supportsMultipleValues);

  if (!requiresValue) return payload;

  if (supportsMultipleValues || opKey === 'in') {
    payload.values = values;
    return payload;
  }

  payload.value = values[0] || '';

  if (requiresSecondValue) {
    payload.valueTo = values[1] || '';
  }

  return payload;
};

export const buildQueryPayload = (definition, operatorCatalog) => {
  const opMap = operatorMapFromCatalog(operatorCatalog);

  return {
    dimensions: (definition.dimensions || []).map((item) => ({
      fieldKey: item.fieldKey,
      alias: item.alias || '',
    })),
    metrics: (definition.metrics || []).map((item) => ({
      fieldKey: item.fieldKey,
      aggregate: String(item.aggregation || 'count').toLowerCase(),
      distinct: Boolean(item.distinct),
      alias: item.alias || '',
    })),
    filters: (definition.filters || []).map((item) =>
      mapFilterPayload(item, opMap.get(String(item.operator || '').toLowerCase()))
    ),
    sort: (definition.sort || []).map((item) => ({
      fieldKey: item.fieldKey,
      direction: String(item.direction || 'asc').toLowerCase(),
    })),
  };
};

export const validateLocalBasics = (definition, operatorCatalog) => {
  const summaryErrors = [];
  const zoneErrors = createEmptyZoneErrors();
  const warnings = [];

  const hasFields =
    (definition?.dimensions?.length || 0) + (definition?.metrics?.length || 0) > 0;
  if (!hasFields) {
    const message = 'Selecciona al menos un campo en Dimensiones o Metricas.';
    summaryErrors.push(message);
    zoneErrors.dimensions = message;
  }

  const metrics = Array.isArray(definition?.metrics) ? definition.metrics : [];
  const invalidMetric = metrics.find((metric) => {
    const allowed = Array.isArray(metric.supportedAggregates)
      ? metric.supportedAggregates
      : [];
    return !allowed.includes(String(metric.aggregation || '').toLowerCase());
  });
  if (invalidMetric) {
    const message = `Agregación inválida para métrica: ${invalidMetric.label}.`;
    summaryErrors.push(message);
    zoneErrors.metrics = message;
  }

  const selectedFieldKeys = new Set([
    ...((definition?.dimensions || []).map((item) => item.fieldKey)),
    ...((definition?.metrics || []).map((item) => item.fieldKey)),
  ]);
  const invalidSort = (definition?.sort || []).find(
    (item) => !selectedFieldKeys.has(item.fieldKey)
  );
  if (invalidSort) {
    const message = 'Sort debe referenciar campos ya seleccionados en Dimensiones o Métricas.';
    summaryErrors.push(message);
    zoneErrors.sort = message;
  }

  const opMap = operatorMapFromCatalog(operatorCatalog);
  const invalidFilter = (definition?.filters || []).find((filter) => {
    const operatorMeta = opMap.get(String(filter.operator || '').toLowerCase());
    const dataType = normalizeDataType(filter.dataType);
    const values = cleanValues(filter.values);

    if (!operatorMeta) return true;

    if (operatorMeta.requiresValue && values.length === 0) return true;
    if (operatorMeta.requiresSecondValue && values.length < 2) return true;

    const dateOps = ['between', 'gt', 'gte', 'lt', 'lte'];
    if (dateOps.includes(String(filter.operator || '').toLowerCase()) && dataType !== 'date') {
      return true;
    }

    return false;
  });

  if (invalidFilter) {
    const message = `Filtro inválido para campo: ${invalidFilter.label}.`;
    summaryErrors.push(message);
    zoneErrors.filters = message;
  }

  return {
    isValid: summaryErrors.length === 0,
    summaryErrors,
    zoneErrors,
    warnings,
  };
};
